/*
 * Live workshop slide registry — "The Unwritten 80% · Cedar & Thread" deck.
 *
 * Single source of truth is ../../../../slides (shared with the facilitator
 * materials): workshop-manifest.json defines order and storage keys, polls.json defines
 * optional slide polls, and the Markdown files hold titles and projected content. The old
 * placeholder registry remains at ../slides but is no longer referenced.
 */
import manifest from '../../../../slides/workshop-manifest.json'
import pollsData from '../../../../slides/polls.json'

const rawFiles = import.meta.glob('../../../../slides/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
})

const rawByName = {}
for (const [path, content] of Object.entries(rawFiles)) {
  rawByName[path.split('/').pop()] = content
}

const pollById = {}
for (const s of pollsData.slides) {
  pollById[s.slideId] = s.poll
}

/**
 * Extract the student-facing content from a slide Markdown file:
 * strip the YAML frontmatter, drop everything from "## Quiz" or
 * "## Presenter notes" onward (including slides without a poll),
 * and remove the leading H1 (SlideChrome renders the title separately).
 */
export function extractBody(raw) {
  if (!raw) return ''
  let s = String(raw)
  s = s.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n/, '')
  const presenterIndex = s.search(/^##[ \t]+(?:Quiz|Presenter notes)\b/m)
  if (presenterIndex !== -1) s = s.slice(0, presenterIndex)
  s = s.replace(/^\s+/, '').replace(/^#\s+.*\r?\n/, '')
  return s.trim()
}

/** Markdown title metadata takes precedence over the manifest's fallback title. */
export function extractTitle(raw, fallback = '') {
  const source = String(raw || '')
  const frontmatter = source.match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/)?.[1]
  const value = frontmatter?.match(/^title:[ \t]*(.+)$/m)?.[1]?.trim()
  if (value) {
    // Deck titles are single-line strings: double-quoted, single-quoted, or plain.
    if (value.startsWith('"')) {
      try {
        const title = JSON.parse(value)
        if (typeof title === 'string' && title.trim()) return title.trim()
      } catch { /* fall back to the Markdown heading */ }
    } else if (value.startsWith("'") && value.endsWith("'")) {
      const title = value.slice(1, -1).replace(/''/g, "'").trim()
      if (title) return title
    } else if (!/^[>|]/.test(value)) {
      return value
    }
  }
  const body = source.replace(/^---\r?\n[\s\S]*?\r?\n---(?:\r?\n|$)/, '')
  return body.match(/^\s*#[ \t]+([^\r\n]+)/)?.[1]?.trim() || fallback
}

export const slides = manifest.slides.map((s, index) => ({
  id: s.id,
  storageId: s.storageId,
  number: index + 1,
  title: extractTitle(rawByName[s.file], s.title),
  file: s.file,
  minutes60: s.minutes60,
  minutes90: s.minutes90,
  bookChapter: s.bookChapter,
  poll: pollById[s.id] || null,
  body: extractBody(rawByName[s.file] || ''),
}))

export function getSlide(id) {
  return slides.find((s) => s.id === id) || null
}

export function getSlideIndex(id) {
  return slides.findIndex((s) => s.id === id)
}

/** Slide IDs, filename prefixes, and URLs all use presentation numbers. */
export function getSlideByNumber(number) {
  if (!/^\d{1,2}$/.test(String(number))) return null
  return slides[Number(number) - 1] || null
}

export function getSlidePath(id) {
  const slide = getSlide(id)
  return slide ? `/slides/${String(slide.number).padStart(2, '0')}` : null
}

/** Persisted progress uses storageId, which must never be interpreted as a slide number. */
export function getSlidePathForProgress(storageId) {
  const slide = slides.find((s) => s.storageId === storageId)
  return slide ? getSlidePath(slide.id) : null
}
